import { AddRounded, CancelRounded, CheckCircleRounded, DoNotDisturbRounded, FiberNewRounded, FilterListRounded, MopedRounded, PersonSearchRounded, RunCircleRounded } from "@mui/icons-material";
import { Container, Paper, Box, Typography, Table, TableCell, TableContainer, TableHead, TableRow, TableBody, Chip, CircularProgress, FormControl, MenuItem, Select, TextField, SelectChangeEvent, IconButton, Tooltip, Button, Stack, Badge, ListItemIcon, Menu, TablePagination } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { ReceiptRequest, ReceiptRequestType } from "../../types/ReceiptRequest";
import { RequestStatus } from "../../types/Type";
import { queryReceiptRequests } from "../../utils/queryReceiptRequests";
import { updateReceiptRequest } from "../../utils/updateReceiptRequest";
import { ReceiptRequestDialog } from "./ReceiptRequestDialog";
import { ChipIcon, ChipColor } from '../ChipUtils';
import { Search, SearchIconWrapper, StyledInputBase } from "../Search";
import { QueryDocumentSnapshot, limit, Query, query, endBefore, limitToLast, startAfter, startAt, collection, getCountFromServer, getDocs, orderBy, where, getFirestore } from "firebase/firestore";
import { MedicalPaperRequest } from "../../types/MedicalPaperRequest";
import { PassportRequest } from "../../types/PassportRequest";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import receiptRequestConverter from "../../converters/receiptRequestConverter";
import { SecureComponent } from "../../security/SecureComponent";
import { hasPermission } from "../../security/Permission";
import { useAppSelector } from "../../store";

const getBadgeIconFromStatus = (status: RequestStatus | 'All') => {
  if (status === 'All') return undefined;
  else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  else if (status === 'Approved') return <CheckCircleRounded color='success' />
  else if (status === 'Denied') return <DoNotDisturbRounded color='error' />
  else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};

const getIconFromStatus = (status: RequestStatus) => {
  if (status === 'Processing') return <RunCircleRounded />;
  else if (status === 'Cancelled') return <CancelRounded />;
  else if (status === 'Approved') return <CheckCircleRounded color='success' />
  else if (status === 'Denied') return <DoNotDisturbRounded color='error' />
  return <FiberNewRounded />;
};

const getColorFromStatus = (status: RequestStatus) => {
  if (status === 'Processing') return 'warning';
  else if (status === 'Cancelled') return 'error';
  else if (status === 'Approved') return <CheckCircleRounded color='success' />
  else if (status === 'Denied') return <DoNotDisturbRounded color='error' />
  return 'info';
};

const Noti = (status: RequestStatus) => {
  if (status === 'New')
    return 'New request has been added!';
  else if (status === 'Processing')
    return 'Request is now being processed!';
  else if (status === 'Approved')
    return 'Request has been approved!'
  else if (status === 'Denied')
    return 'Request has been denied!';
  else
    return 'Request has been cancelled!';
};

interface ReceiptRequestsProps {
  type: ReceiptRequestType
};
export const ReceiptRequestsPage = (props: ReceiptRequestsProps) => {
  const { type } = props;
  const db = getFirestore();
  const authState = useAppSelector(s => s.auth);
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState('Iqama Renewal Receipt');

  const [receiptRequests, setReceiptRequests] = useState<ReceiptRequest[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<RequestStatus | 'All'>('New');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [firstRow, setFirsRow] = useState<QueryDocumentSnapshot | null>(null);
  const [lastRow, setLastRow] = useState<QueryDocumentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  const [openRequest, setOpenRequest] = useState<ReceiptRequest | undefined>();

  const refresh = (callback?: (q: Query<ReceiptRequest>) => Query<ReceiptRequest>) => {
    setLoading(true);
    let q: Query<ReceiptRequest> = query(collection(db, `ReceiptRequests`), where('type', '==', type)).withConverter(receiptRequestConverter);

    if (search.trim() === '') 
      q = query(q, orderBy('dateRequested', 'asc'));
    else
      q = query(q, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));

    if(callback)
      q = callback(q);
    if (filter !== 'All')
      q = query(q, where('status', '==', filter));
    getDocs(q).then(qSnapshot => {
      setReceiptRequests(qSnapshot.docs.map(d => d.data()));
      setFirsRow(qSnapshot.docs[0]);
      setLastRow(qSnapshot.docs[qSnapshot.docs.length - 1]);
      setLoading(false);
    });

    // Count query
    let qCount: Query = query(collection(db, `ReceiptRequests`), where('type', '==', type));
    if (search.trim() === '') 
      qCount = query(qCount, orderBy('dateRequested', 'asc'));
    else
      qCount = query(qCount, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));
    if (filter !== 'All') 
      qCount = query(qCount, where('status', '==', filter));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const searchDebouncedCallback = useDebouncedCallback((value) => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, 1000);

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    searchDebouncedCallback(event.currentTarget.value);
  };

  const handleFilterButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuOnClose = () => setFilterAnchorEl(null);

  const applyFilter = (f: RequestStatus | 'All') => {
    setFilter(f);
    setFilterAnchorEl(null);
  }
  
  const filterAll = () => applyFilter('All');
  const filterNew = () => applyFilter('New');
  const filterProcessing = () => applyFilter('Processing');
  const filterApproved = () => applyFilter('Approved');
  const filterDenied = () => applyFilter('Denied');
  const filterCancelled = () => applyFilter('Cancelled');

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastRow), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstRow), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstRow), limit(rowsPerPage));
      });
    }
    setPage(to);
  };
  
  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const approveDenyRequest = (receiptRequest: ReceiptRequest, status: RequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateReceiptRequest(receiptRequest, status).then(() => {
        setReceiptRequests(receiptRequests.map(r => {
          if (r.id === receiptRequest.id) {
            return { ...r, status};
          }

          return r;
        }));

        enqueueSnackbar(`Request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  const handleRowOnClick = (receiptRequest: ReceiptRequest) => {
    setOpenRequest(receiptRequest);
  };

  const handleApproveOnClick = (event: MouseEvent<HTMLButtonElement>, receiptRequest: ReceiptRequest) => {
    event.stopPropagation();
    approveDenyRequest(receiptRequest, 'Approved');
  };

  const handleDenyOnClick = (event: MouseEvent<HTMLButtonElement>, receiptRequest: ReceiptRequest) => {
    event.stopPropagation();
    approveDenyRequest(receiptRequest, 'Denied');
  };

  useEffect(() => {
    if (type === 'Iqama')
      setTitle('Iqama Renewal Receipt');
    else if (type === 'Vacation')
      setTitle('Tashera Payments');
    else
      setTitle('Other Files');
    
    refresh(q => query(q, limit(rowsPerPage)));
    // queryReceiptRequests(type, status).then((receiptRequests) => {
    //   setReceiptRequests(receiptRequests);
    //   setReceiptRequestsFiltered(receiptRequests);
    // });
  }, [type, filter, rowsPerPage]);

  return (
    <>
      <Container disableGutters>
        <Typography variant="h5" mt={2} mb={5}>{title}</Typography>
        <Stack direction='row' mb={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          {/* <Button startIcon={<AddRounded />} onClick={handleFormOpen}>Create New Request</Button> */}
        </Stack>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={120}>Id Number</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={220}>Date Requested</TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Status
                      </Box>
                      <Tooltip title='Filter Status'>
                        <IconButton size='small' onClick={handleFilterButtonOnClick}>
                          <Badge badgeContent={filter !== 'All' ? getBadgeIconFromStatus(filter) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                    {(
                    (type === 'Iqama' && hasPermission(authState, [], ['update.iqamaRenewalReceipts'])) ||
                    (type === 'Vacation' && hasPermission(authState, [], ['update.tasheraPayments'])) ||
                    (type === 'Others' && hasPermission(authState, [], ['update.otherFiles']))
                    ) && (
                      <TableCell width={112} align="center">Actions</TableCell>
                    )}
                </TableRow>
              </TableHead>
              <TableBody>
                {receiptRequests.map((r, i) => {
                  return (
                    <TableRow 
                      hover 
                      key={r.id} 
                      onClick={() => handleRowOnClick(r)}
                      sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="right">{i + 1}</TableCell>
                      <TableCell>{r.idNumber}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{DateTime.fromISO(r.dateRequested).toLocaleString(DateTime.DATETIME_MED)}</TableCell>
                      <TableCell>
                        <Chip icon={ChipIcon(r.status)} label={r.status} color={ChipColor(r.status)} variant="outlined" />
                      </TableCell>
                      {(
                      (type === 'Iqama' && hasPermission(authState, [], ['update.iqamaRenewalReceipts'])) ||
                      (type === 'Vacation' && hasPermission(authState, [], ['update.tasheraPayments'])) ||
                      (type === 'Others' && hasPermission(authState, [], ['update.otherFiles']))
                      ) && (
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                            <Tooltip title="Approve Request">
                              <span>
                                <IconButton color="success" onClick={(e) => handleApproveOnClick(e, r)} disabled={r.status !== 'New' && r.status !== 'Processing'}>
                                  <CheckCircleRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Deny Request">
                              <span>
                                <IconButton color="error" onClick={(e) => handleDenyOnClick(e, r)} disabled={r.status !== 'New' && r.status !== 'Processing'}>
                                  <DoNotDisturbRounded />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
        <ReceiptRequestDialog receiptRequest={openRequest}  onClose={() => setOpenRequest(undefined)}/>
      </Container>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterNew}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          New
        </MenuItem>
        <MenuItem onClick={filterProcessing}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Processing
        </MenuItem>
        <MenuItem onClick={filterApproved}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Approved
        </MenuItem>
        <MenuItem onClick={filterDenied}>
          <ListItemIcon>
            <DoNotDisturbRounded color='error' />
          </ListItemIcon>
          Denied
        </MenuItem>
        <MenuItem onClick={filterCancelled}>
          <ListItemIcon>
            <CancelRounded color='error' />
          </ListItemIcon>
          Cancelled
        </MenuItem>
      </Menu>
    </>
  )
};